import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button, Checkbox, Loading, TextInput } from '@carbon/react';
import { Checkmark, Close, Launch } from '@carbon/react/icons';
import {
  autoResolveOverlapsAtom,
  builderNameCheckAtom,
  builderUnitNameAtom,
} from './atoms';
import style from './unit-builder-modal.module.scss';

interface UnitBuilderHeaderProps {
  /** Scoped unit's name; falls back to draft copy when there is no unit yet. */
  unitName: string | null;
  /** The prev/next pager. Slot rather than a prop so the shell stays unaware
   *  of how paging is sourced. */
  pager?: ReactNode;
  /** Opens the scoped unit in its own tab. Omitted while there is no unit. */
  unitHref?: string;
  /** Show the "Auto-resolve overlaps" toggle — cloned/unsaved units only. When on,
   *  saving force-keeps the new unit through FOUCS. */
  showAutoResolve?: boolean;
  /**
   * Allow renaming — unsaved drafts only (a clone or a brand-new unit). A
   * persisted unit is read-only here: the builder's Save only ever CREATEs, so
   * there is no path that could rename one.
   */
  nameEditable?: boolean;
  onClose: () => void;
}

/**
 * Unit Builder title bar: the scoped unit, a slot for the pager, and close.
 *
 * @param {UnitBuilderHeaderProps} props - Unit identity, pager slot and close.
 * @return {JSX.Element} The header.
 */
export const UnitBuilderHeader: FC<UnitBuilderHeaderProps> = ({
  unitName,
  pager,
  unitHref,
  showAutoResolve = false,
  nameEditable = false,
  onClose,
}) => {
  const [autoResolve, setAutoResolve] = useAtom(autoResolveOverlapsAtom);

  // The name the draft will be created under. Typed straight into the atom so the
  // map pane's dry run re-checks it as the user types (debounced there) — the
  // verdict comes back through `builderNameCheckAtom`. `null` = untouched, so the
  // scoped unit's own name shows.
  const [draftName, setDraftName] = useAtom(builderUnitNameAtom);
  const nameCheck = useAtomValue(builderNameCheckAtom);
  const [editing, setEditing] = useState(false);
  // What is being typed. Local on purpose: writing every keystroke to
  // `builderUnitNameAtom` re-fired the map's dry run as you typed. The atom is
  // now only written on commit, so the name is checked once, on the tick.
  const [typed, setTyped] = useState('');
  // Commit is pending a verdict: the input stays open until the dry run answers,
  // so a taken name does not close the field.
  const [checking, setChecking] = useState(false);
  // The dry run debounces before it starts, so `loading` is still false right
  // after a commit. Wait for it to go true and back to false before judging.
  const sawLoadingRef = useRef(false);
  // What to restore if the edit is abandoned.
  const revertToRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Enter / Escape unmount the input, which fires `onBlur` — without this the
  // blur handler would commit or revert a second time.
  const skipBlurRef = useRef(false);

  const displayName = draftName ?? unitName;
  const trimmed = typed.trim();
  // The dry run reports every reason the unit could not be created — a taken
  // name, but also shape refusals (contiguity, section count). Only the name ones
  // belong to this field: a non-contiguous boundary must not block a rename.
  const nameErrors = nameCheck.errors.filter((message) =>
    /\bname\b/i.test(message)
  );

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  // Leaving edit mode (unit paged away, or renaming switched off) must not strand
  // the input open over another unit.
  useEffect(() => {
    if (!nameEditable) setEditing(false);
  }, [nameEditable]);

  const beginEdit = () => {
    revertToRef.current = draftName;
    // Seed from the shown name so the field opens with what the user sees.
    setTyped(displayName ?? '');
    setEditing(true);
  };
  // Submits the name for checking. The field closes only once the dry run comes
  // back clean — see the effect below.
  const commit = () => {
    if (trimmed === '') return;
    skipBlurRef.current = true;
    sawLoadingRef.current = false;
    setChecking(true);
    setDraftName(trimmed);
  };
  const cancel = () => {
    skipBlurRef.current = true;
    setChecking(false);
    setDraftName(revertToRef.current);
    setEditing(false);
  };

  // Resolve a pending commit. A name error keeps the field open with the reason;
  // anything else closes it. The 3s fallback covers the case where no dry run can
  // run at all (no boundary yet), which would otherwise hang the field open.
  useEffect(() => {
    if (!checking) return undefined;
    if (nameCheck.loading) {
      sawLoadingRef.current = true;
      return undefined;
    }
    if (sawLoadingRef.current) {
      setChecking(false);
      if (nameErrors.length === 0) setEditing(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setChecking(false);
      setEditing(false);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [checking, nameCheck.loading, nameErrors.length]);

  return (
    <div className={style.header}>
      {/* Identity, launch and pager read together on the left, as in the mock's
        `‹ Unit Builder — U-… ⧉ (37/111) ›`. Close sits hard right. */}
      <div className={style.headerLeft}>
        <h2 className={style.title}>
          Unit Builder
          {editing ? (
            <>
              <span className={style.titleSeparator}>—</span>
              <span className={style.nameEdit}>
                <TextInput
                  ref={inputRef}
                  id="unit-builder-unit-name"
                  size="sm"
                  labelText="Unit name"
                  hideLabel
                  className={style.nameInput}
                  value={typed}
                  invalid={trimmed !== '' && nameErrors.length > 0}
                  invalidText={nameErrors.join(' ')}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commit();
                    } else if (event.key === 'Escape') {
                      event.preventDefault();
                      cancel();
                    }
                  }}
                  onBlur={() => {
                    if (skipBlurRef.current) {
                      skipBlurRef.current = false;
                      return;
                    }
                    // Mid-check, leave it alone — the effect owns the outcome.
                    if (checking) return;
                    // Clicking away abandons the rename. It deliberately does NOT
                    // commit: committing writes the name atom, which is what
                    // triggers the dry run, and only the tick may do that.
                    cancel();
                  }}
                />
                {/* The guard sits on the buttons, not the wrapper: on the
                    wrapper it also swallowed the mousedown that places the caret,
                    so the field could not be clicked into. Here it only stops the
                    blur-cancel beating the button's own click. */}
                {checking ? (
                  // Takes the tick's place while the name is being checked, so
                  // the wait is visible and the field cannot be re-submitted.
                  <span className={style.nameChecking}>
                    <Loading
                      small
                      withOverlay={false}
                      description="Checking name"
                    />
                  </span>
                ) : (
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    iconDescription="Save name"
                    renderIcon={Checkmark}
                    disabled={trimmed === ''}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={commit}
                  />
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  iconDescription="Cancel rename"
                  renderIcon={Close}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={cancel}
                />
              </span>
            </>
          ) : (
            <>
              {displayName ? (
                <>
                  <span className={style.titleSeparator}>—</span>
                  {nameEditable ? (
                    <button
                      type="button"
                      className={style.nameButton}
                      title="Click to rename this unit"
                      onClick={beginEdit}
                    >
                      {displayName}
                    </button>
                  ) : (
                    <span className={style.unitName}>{displayName}</span>
                  )}
                </>
              ) : nameEditable ? (
                <button
                  type="button"
                  className={`${style.nameButton} ${style.draftNote}`}
                  title="Click to name this unit"
                  onClick={beginEdit}
                >
                  — new unit
                </button>
              ) : (
                <span className={style.draftNote}>— new unit</span>
              )}
            </>
          )}
        </h2>

        {unitHref && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription="Open unit in a new tab"
            href={unitHref}
            target="_blank"
            rel="noreferrer"
            renderIcon={Launch}
            className={style.launchButton}
          />
        )}

        {pager && <div className={style.pagerSlot}>{pager}</div>}

        {/* Cloned/unsaved units only: force-keep the new unit through FOUCS on save
          so it isn't dropped for overlap. */}
        {showAutoResolve && (
          <Checkbox
            id="unit-builder-auto-resolve"
            className={style.autoResolveToggle}
            labelText="Auto-resolve overlaps"
            checked={autoResolve}
            onChange={(_evt, { checked }) => setAutoResolve(checked)}
          />
        )}
      </div>

      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        iconDescription="Close Unit Builder"
        renderIcon={Close}
        onClick={onClose}
        className={style.closeButton}
      />
    </div>
  );
};
